/**
 * Hazard.ts — Roaming critters (cats, foxes, squirrels, pigeons).
 *
 * Each hazard bounds around the map on a random heading that it re-rolls on a
 * timer, bouncing off the world bounds and buildings. Every one of them winds
 * the dog up the same way — that rule lives in systems/HazardManager.ts — and
 * what tells them apart is **what they do back**:
 *
 *   - squirrel → freezes for a beat, then bolts (`flee`).
 *   - fox      → stands its ground, ears back, and does not move (`hold`).
 *   - cat      → holds like the fox, until the dog closes inside
 *                `HAZARD.PANIC_RADIUS`, and then breaks and runs (`nerve`).
 *   - pigeon   → ignores a dog walking past entirely, and takes off at the
 *                first **bark**, flying somewhere else (`flush`).
 *
 * The map from species to reaction is `HAZARD.REACTION`; the reactions
 * themselves are `CritterReaction` in types.ts. Visually they hop rather than
 * glide (the pigeon walks, and flaps), and they flatten their ears when
 * something scares them. This class is the model, its wander AI, and its
 * reactions; the proximity and bark rules that trigger them are the manager's.
 */
import { HAZARD } from '../config/constants';
import { activeLevel } from '../config/level';
import { Entity } from './Entity';
import { isMoving } from './locomotion';
import {
  createHazard,
  animateWalk,
  animateHop,
  animateFlap,
  animateTail,
  applyStartle,
  type CharacterRig,
} from '../render/models';
import { progress01, easeOutCubic, arc01 } from '../utils/easing';
import type { CritterReaction, HazardType } from '../types';

export class Hazard extends Entity {
  readonly hazardType: HazardType;
  /** What this critter does about the dog. See `CritterReaction`. */
  readonly reaction: CritterReaction;
  private rig: CharacterRig;
  private retargetTimer = 0;

  /** Counts down while the critter is reacting to the dog being close. */
  private startleMs = 0;
  /** True once this startle has already triggered the bolt (fires once). */
  private bolted = false;
  /**
   * Whether *this* scare ends in a run. A squirrel's always does; a fox's never
   * does; a cat's does only if the dog was inside `PANIC_RADIUS` at some point
   * during the reaction. Latched per scare rather than re-decided per frame, so
   * a cat that has already broken keeps running instead of stopping the moment
   * its own bolt carries it back out of the panic radius.
   */
  private willBolt = false;

  /** Counts down while the bird is off the ground. 0 = on its feet. */
  private flightMs = 0;
  /** Height above the ground plane, driven by the flight arc. */
  private altitude = 0;

  constructor(x: number, y: number, type: HazardType) {
    const rig = createHazard(type);
    super(x, y, HAZARD.SIZE / 2, rig.group);
    this.hazardType = type;
    this.reaction = HAZARD.REACTION[type];
    this.rig = rig;
    this.pickNewHeading();
  }

  /**
   * Choose a fresh random direction + speed.
   *
   * The speed band and the retarget timing come from the **level**, not from
   * `HAZARD` — a harder level's wildlife is quicker on its feet. Everything else
   * about how a critter behaves is the same wherever it is.
   */
  private pickNewHeading(): void {
    const { speedMin, speedMax, retargetMinMs, retargetJitterMs } = activeLevel().hazards;
    const angle = Math.random() * Math.PI * 2;
    const speed = speedMin + Math.random() * (speedMax - speedMin);
    this.vel.x = Math.cos(angle) * speed;
    this.vel.y = Math.sin(angle) * speed;
    this.retargetTimer = retargetMinMs + Math.random() * retargetJitterMs;
  }

  /**
   * The dog has come within startle range, from (dogX, dogY).
   *
   * Every reaction starts the same way — freeze, ears flat — and they part
   * company at the end of the anticipation beat: see `willBolt`. A `flush`
   * critter has no proximity reaction at all and a bird already in the air has
   * nothing left to react with, so both are ignored.
   *
   * Returns true only when this call started a *fresh* reaction, so the caller
   * can make a noise about it exactly once per scare.
   *
   * The re-entrant case matters and is not just a guard: `HazardManager` calls
   * this every frame the dog is in range, and a cat that is holding its ground
   * has to be able to *upgrade* to a bolt when the dog keeps coming. Anything
   * else and the cat's whole rule is unreachable.
   */
  startle(dogX: number, dogY: number): boolean {
    if (this.reaction === 'flush' || this.flightMs > 0) return false;
    const panicking =
      this.reaction === 'flee' ||
      (this.reaction === 'nerve' && Math.hypot(this.pos.x - dogX, this.pos.y - dogY) <= HAZARD.PANIC_RADIUS);

    if (this.startleMs > 0) {
      // Already reacting. The only thing left to change is a nerve critter's
      // mind, and only in the direction of running.
      if (panicking) this.willBolt = true;
      return false;
    }

    this.startleMs = HAZARD.STARTLE_MS;
    this.bolted = false;
    this.willBolt = panicking;
    // Freeze for the anticipation beat.
    this.vel.x = 0;
    this.vel.y = 0;
    this.fleeFrom.x = dogX;
    this.fleeFrom.y = dogY;
    return true;
  }

  /**
   * A bark went off at (fromX, fromY): take off and fly somewhere else.
   *
   * Only birds do this, and only from the ground. The flight is a straight line
   * away from the noise over `HAZARD.FLIGHT_MS`, carried on the ordinary
   * velocity integration so nothing downstream needs a special case — the
   * manager simply stops resolving it against buildings while it is up there,
   * because a bird flies over a roof.
   *
   * Returns true if it actually took off.
   */
  flush(fromX: number, fromY: number): boolean {
    if (this.reaction !== 'flush' || this.flightMs > 0) return false;
    // Away from the bark, with a little scatter, so a flushed flock fans out
    // rather than leaving as one arrow. A bird sitting exactly on the dog gets
    // atan2(0, 0) = 0, which is a direction like any other — there is nothing
    // to divide by here, so nothing to guard.
    const angle =
      Math.atan2(this.pos.y - fromY, this.pos.x - fromX) + (Math.random() - 0.5) * 1.1;
    this.vel.x = Math.cos(angle) * HAZARD.FLIGHT_SPEED;
    this.vel.y = Math.sin(angle) * HAZARD.FLIGHT_SPEED;
    this.flightMs = HAZARD.FLIGHT_MS;
    // A startled bird is a flying bird; it has no freeze beat to serve.
    this.startleMs = 0;
    return true;
  }

  /** Where the scare came from. Only read while startled, which can only be
   *  entered through startle() above — so it is always current when used. */
  private fleeFrom = { x: 0, y: 0 };

  get isStartled(): boolean {
    return this.startleMs > 0;
  }

  /** Off the ground, and so out of the dog's reach and over the buildings. */
  get isFlying(): boolean {
    return this.flightMs > 0;
  }

  update(deltaMs: number): void {
    if (this.flightMs > 0) {
      this.flightMs -= deltaMs;
      // One sine arc: up, over, and down onto its feet again. The velocity is
      // unchanged throughout — only the height is animated, so the bird's
      // ground track is the same straight line the collision code already
      // understands.
      const flown = progress01(HAZARD.FLIGHT_MS - this.flightMs, HAZARD.FLIGHT_MS);
      this.altitude = arc01(flown) * HAZARD.FLIGHT_HEIGHT;
      if (this.flightMs <= 0) {
        this.flightMs = 0;
        this.altitude = 0;
        this.pickNewHeading(); // lands and carries on pecking about
      }
      return;
    }

    if (this.startleMs > 0) {
      this.startleMs -= deltaMs;
      // After the freeze beat, whatever is going to run, runs.
      if (!this.bolted && this.startleMs <= HAZARD.STARTLE_MS - HAZARD.STARTLE_FREEZE_MS) {
        this.bolted = true;
        if (this.willBolt) {
          const dx = this.pos.x - this.fleeFrom.x;
          const dy = this.pos.y - this.fleeFrom.y;
          const len = Math.hypot(dx, dy) || 1;
          this.vel.x = (dx / len) * HAZARD.STARTLE_SPEED;
          this.vel.y = (dy / len) * HAZARD.STARTLE_SPEED;
          this.retargetTimer = HAZARD.RETARGET_MIN_MS;
        }
      }
      return; // a startled critter ignores its normal wander timer
    }

    this.retargetTimer -= deltaMs;
    if (this.retargetTimer <= 0) this.pickNewHeading();
  }

  /** Reverse out of an obstacle after a collision push-out. */
  bounce(): void {
    this.vel.x *= -1;
    this.vel.y *= -1;
  }

  /**
   * Lift the rig by the flight arc — the one thing here that isn't on the
   * ground plane.
   *
   * The `?? 0` is load-bearing: `Entity`'s constructor calls `syncMesh()`, and
   * a subclass's field initialisers have not run at that point, so `altitude`
   * is genuinely undefined for that one call. Without it every critter is born
   * at y = NaN and never draws.
   */
  syncMesh(): void {
    super.syncMesh();
    this.object.position.y = this.altitude ?? 0;
  }

  updateVisuals(dt: number, t: number): void {
    this.advanceAnimation(dt);
    const moving = isMoving(this.vel.x, this.vel.y);
    // Legs keep their swing, but the body bounds in arcs with squash-and-stretch.
    animateWalk(this.rig, this.walkPhase, moving, 0.35, 0);
    // A bird walks; it does not bound. Its lift comes from the wings instead.
    if (this.rig.wings) animateFlap(this.rig, t, this.isFlying);
    else animateHop(this.rig, this.walkPhase, moving);
    animateTail(this.rig, t, this.isStartled ? 0.2 : 0.6);

    if (this.startleMs > 0) {
      // Ears flatten hardest at the moment of the scare, then recover.
      const intensity = 1 - easeOutCubic(progress01(HAZARD.STARTLE_MS - this.startleMs, HAZARD.STARTLE_MS));
      applyStartle(this.rig, intensity);
    }
    this.syncMesh();
  }
}
